import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { Database } from "@/integrations/supabase/types";
import { FullPageLoader } from "@/components/ui/loading-spinner";

type AppRole = Database["public"]["Enums"]["app_role"];

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
  redirectTo?: string;
}

export const ProtectedRoute = ({ 
  children, 
  allowedRoles,
  redirectTo = "/auth" 
}: ProtectedRouteProps) => {
  const { user, loading: authLoading, sessionExpired } = useAuth();
  const { role, loading: roleLoading, getDashboardPath } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authLoading || roleLoading) return;

    // Redirect to auth if session expired or no user
    if (sessionExpired || !user) {
      // Store the intended destination for redirect after login
      const returnTo = location.pathname !== "/auth" ? location.pathname : undefined;
      navigate(redirectTo, { 
        replace: true,
        state: returnTo ? { from: returnTo } : undefined 
      });
      return;
    }

    if (allowedRoles && role && !allowedRoles.includes(role)) {
      navigate(getDashboardPath(), { replace: true });
    }
  }, [user, role, authLoading, roleLoading, sessionExpired, allowedRoles, navigate, redirectTo, getDashboardPath, location.pathname]);

  if (authLoading || roleLoading) {
    return <FullPageLoader text="Checking authentication..." />;
  }

  if (!user || sessionExpired) {
    return null;
  }

  if (allowedRoles && role && !allowedRoles.includes(role)) {
    return null;
  }

  return <>{children}</>;
};
